// Self-contained line-glyph set for the onboarding checklist, ported from the
// design prototype so every step/indicator reads as one icon family. All glyphs
// inherit `currentColor` and scale with `size`.

// ------------------------------------------------------------- step glyphs
const STEP_PATHS = {
  shield: (
    <>
      <path
        d="M12 3.2l7 2.4v5.1c0 4.4-3 7.3-7 8.9-4-1.6-7-4.5-7-8.9V5.6l7-2.4z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M9 11.8l2.1 2.1L15.2 10"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  ),
  card: (
    <>
      <rect x="3.5" y="6" width="17" height="12" rx="2.2" stroke="currentColor" strokeWidth="1.7" />
      <path d="M3.5 10h17" stroke="currentColor" strokeWidth="1.7" />
      <path d="M7 14.4h4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </>
  ),
  building: (
    <>
      <path
        d="M4 20V6.5A1.5 1.5 0 015.5 5H12a1.5 1.5 0 011.5 1.5V20"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M13.5 9.5H18a1.5 1.5 0 011.5 1.5V20"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <path
        d="M3 20h18M7 9h3M7 12.5h3M7 16h3"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </>
  ),
  types: (
    <>
      <path
        d="M9 6.5h11M9 12h11M9 17.5h11"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <circle cx="4.6" cy="6.5" r="1.5" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="4.6" cy="12" r="1.5" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="4.6" cy="17.5" r="1.5" stroke="currentColor" strokeWidth="1.6" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.8" y="5" width="16.4" height="15" rx="2.2" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M3.8 9.3h16.4M8 3.2v3.6M16 3.2v3.6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <path
        d="M7.5 13h3M13.5 13h3M7.5 16.4h3"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M3.6 12h16.8M12 3.5c2.4 2.3 3.6 5.3 3.6 8.5S14.4 18.2 12 20.5c-2.4-2.3-3.6-5.3-3.6-8.5S9.6 5.8 12 3.5z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </>
  ),
  truck: (
    <>
      <path
        d="M3 7.5h11v9H3zM14 10.5h3.5L21 14v2.5h-7"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
      <circle cx="7" cy="17.5" r="1.9" stroke="currentColor" strokeWidth="1.7" />
      <circle cx="17" cy="17.5" r="1.9" stroke="currentColor" strokeWidth="1.7" />
    </>
  ),
  staff: (
    <>
      <circle cx="9" cy="9" r="3.1" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M3.3 19c.7-3 3.1-4.9 5.7-4.9s5 1.9 5.7 4.9"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <path
        d="M16.5 6.4a2.9 2.9 0 010 5.5M17.5 14.4c2 .5 3.5 2.2 3.9 4.6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="8" r="3.6" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M10.6 10.6l7 7M15.4 16l1.8-1.8M17.2 17.8l1.8-1.8"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>
  )
};

export const StepGlyph = ({ name, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {STEP_PATHS[name] || STEP_PATHS.building}
  </svg>
);

// ------------------------------------------------------------- ui icons
export const Icon = {
  check: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path
        d="M5 12.5l4.4 4.4L19 7.2"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  lock: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <rect x="5" y="10.5" width="14" height="9.5" rx="2.2" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8 10.5V8a4 4 0 018 0v2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  arrowOut: (s = 15) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path
        d="M8 16L16 8M9 8h7v7"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  sparkle: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
    </svg>
  ),
  clock: (s = 14) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 7.5V12l3 2"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  chevronDown: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
};
